import { cofaceAccessTypes } from 'shared/constants/creditReview/cofaceAccessTypes';
import { orderTypes } from 'shared/constants/orderTypes';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { RatingStatus } from 'shared/types/ratingStatus';
import { ArtifactIdType } from 'shared/constants/artifactIdType';
import {
  PortfolioEntity,
  ScoreData,
  SigProduct,
  SubScore,
} from '../../modules/common/PortfolioManager/types';

export const entityPortfolioModuleData = (
  entity: PortfolioEntity,
  sigProduct: SigProduct,
  cofaceAccessType: string,
  esgRequestPermitted: Boolean,
  subscore?: SubScore,
) => {
  const { userId, organisationId } = sigProduct;

  // Logic described in SCI-2890
  const canRequestEsg =
    esgRequestPermitted &&
    (entity.meta.esgCustomScore === null || entity.meta.esgCustomScore === true) &&
    (entity.meta.esgRequestState === null || entity.meta.esgRequestState === RatingStatus.TO_DO) &&
    entity.meta.artifactIdType !== ArtifactIdType.EasyNumber;

  const result: Record<ratingTypes, ScoreData> = {
    [ratingTypes.CREDIT_RATING]: {
      score:
        subscore === 'shortTermRating'
          ? entity.scores.creditRatingShortTermRating
          : entity.scores.creditRating,
      canRequest:
        entity.meta.storefrontSubscriptionType === 'SUBSCRIPTION' ||
        entity.meta.storefrontSubscriptionType === 'FREE_TRIAL',
      canUnlock: entity.meta.storefrontSubscriptionType === 'NONE',
      requested: !!entity.meta.storefrontPurchased,
      product: {
        l4: {
          purchased:
            (entity.meta.storefrontSubscriptionType === 'SUBSCRIPTION' ||
              entity.meta.storefrontSubscriptionType === 'FREE_TRIAL') &&
            !!entity.meta.storefrontPurchased,
        },
        l5: {
          purchased:
            entity.meta.storefrontSubscriptionType === 'PUBLIC' &&
            !!entity.meta.storefrontPurchased,
        },
      },
      orderType: orderTypes.ENTITY,
      requiresDataUpload: false,
    },
    [ratingTypes.CREDIT_REVIEW]: {
      score: entity.scores.creditReview?.toFixed(1),
      canRequest:
        entity.meta.artifactIdType === 'EASYNUMBER' &&
        cofaceAccessType === cofaceAccessTypes.REQUEST_ALLOWED,
      canUnlock:
        entity.meta.artifactIdType === 'EASYNUMBER' &&
        (cofaceAccessType === cofaceAccessTypes.NO_CONTRACT_ID ||
          cofaceAccessType === cofaceAccessTypes.NOT_ENOUGH_CREDITS),
      requested: !!entity.meta.sigRequested,
      product: {
        requested: !!entity.meta.sigRequested,
        userId,
        organisationId,
      },
      orderType: orderTypes.SIG_ENTITY,
      requiresDataUpload: false,
    },
    [ratingTypes.ESG_IMPACT]: {
      score:
        subscore && subscore !== 'shortTermRating'
          ? {
              environmental: entity.scores.esgEnvironment?.toFixed(1),
              social: entity.scores.esgSocial?.toFixed(1),
              governance: entity.scores.esgGovernance?.toFixed(1),
            }[subscore]
          : entity.scores.esgTotal?.toFixed(1),
      canRequest: canRequestEsg,
      canUnlock:
        !entity.meta.esgPurchased &&
        entity.meta.artifactIdType !== ArtifactIdType.EasyNumber &&
        entity.meta.esgRequestState !== 'UNABLE_TO_REQUEST',
      requested: entity.meta.esgRequestState === RatingStatus.IN_PROGRESS,
      requiresDataUpload:
        (entity.meta.esgRequestState === RatingStatus.TO_DO ||
          entity.meta.esgRequestState === RatingStatus.IN_PROGRESS) &&
        !!entity.meta.esgCustomScore,
    },
    [ratingTypes.SENTIMENT]: {
      score: subscore
        ? {
            environmental: entity.scores.sentifi?.eScore?.toFixed(2),
            social: entity.scores.sentifi?.sScore?.toFixed(2),
            governance: entity.scores.sentifi?.gScore?.toFixed(2),
          }[subscore]
        : entity.scores.sentifi?.esgScore?.toFixed(2),
      canRequest: !!(
        entity.meta.esgSentifiHasScore &&
        entity.meta.esgSentifiHasPermission &&
        !entity.meta.esgSentifiHasCompany
      ),
      canUnlock: !!(entity.meta.esgSentifiHasScore && !entity.meta.esgSentifiHasPermission),
      requested:
        entity.meta.esgSentifiRequestState === 'IN_PROGRESS' ||
        !!(
          entity.meta.esgSentifiHasScore &&
          entity.meta.esgSentifiHasCompany &&
          entity.meta.esgSentifiHasPermission
        ),
      requiresDataUpload: false,
    },
    [ratingTypes.CLIMATE_STRESS_TEST]: {
      score: {
        climateLossOrderly: entity.scores.cstOrderly.climateLoss?.toFixed(2),
        climateLossDisorderly: entity.scores.cstDisorderly.climateLoss?.toFixed(2),
        climateLossHotHouse: entity.scores.cstHotHouse.climateLoss?.toFixed(2),
        physicalRiskLossOrderly: entity.scores.cstOrderly.physicalRiskLoss?.toFixed(2),
        physicalRiskLossDisorderly: entity.scores.cstDisorderly.physicalRiskLoss?.toFixed(2),
        physicalRiskLossHotHouse: entity.scores.cstHotHouse.physicalRiskLoss?.toFixed(2),
        transitionRiskLossOrderly: entity.scores.cstOrderly.transitionRiskLoss?.toFixed(2),
        transitionRiskLossDisorderly: entity.scores.cstDisorderly.transitionRiskLoss?.toFixed(2),
        transitionRiskLossHotHouse: entity.scores.cstHotHouse.transitionRiskLoss?.toFixed(2),
      }[subscore!],
      canRequest: canRequestEsg,
      canUnlock:
        !entity.meta.esgPurchased &&
        entity.meta.artifactIdType !== ArtifactIdType.EasyNumber &&
        entity.meta.esgRequestState !== 'UNABLE_TO_REQUEST',
      requested: entity.meta.esgRequestState === RatingStatus.IN_PROGRESS,
      requiresDataUpload:
        (entity.meta.esgRequestState === RatingStatus.TO_DO ||
          entity.meta.esgRequestState === RatingStatus.IN_PROGRESS) &&
        !!entity.meta.esgCustomScore,
    },
    [ratingTypes.FUNDS]: {} as ScoreData,
  };

  return result;
};
